// Driver Refunds: Uber earnings from trips in a car that is not in the Fleetly
// fleet, which Uber paid to Fleetly because the person's account sat on our fleet
// account. Fleetly gave no car, no petrol and no employment for that work, so the
// money is theirs. db/migrations/0800, 0801.
//
// Every figure on this screen comes from Postgres. The page arranges them.
// CLAUDE.md rule 4. A missing figure shows as a dash, never as R0.00, and a
// negative day (more cash collected than Uber paid) shows as negative.
//
// THE ORDER IS THE POINT. The sweep proposes. A person raises a refund, from the
// sweep or from the person's own claim. One person verifies the facts, a second
// approves the amount, and only then is an EFT staged to a verified bank account.
// A cash proof refund skips the verify step (9300): the slip was checked when it
// was accepted on Driver cash, so only the approval is left.
// The buttons follow what the signed-in person may do, and Postgres checks again,
// so a button that should not be there still cannot do anything.
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AlertTriangle, Check, ChevronDown, ChevronRight, HandHeart, RefreshCw, ShieldAlert, X } from 'lucide-react'
import {
  useApproveRefund, useGetRefundOptions, useGetRefundSweep, useGetRefunds, useGetRefundsAccess,
  useMarkRefundPaid, useRaiseRefund, useRejectRefund, useStageRefundPayment, useVerifyRefund,
} from '../hooks/backend/refunds'
import { Button } from '../lib/shadcn/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../lib/shadcn/dialog'
import { Input } from '../lib/shadcn/input'
import { NativeSelect } from '../lib/shadcn/native-select'
import { Spinner } from '../lib/shadcn/spinner'
import { Textarea } from '../lib/shadcn/textarea'
import { cn } from '../lib/shadcn/utils'
import { Chip } from '../components/interviews/Chip'
import { BankDetailsPanel } from '../components/drivers/BankDetailsPanel'
import { formatDay, formatWhen, formatZar } from '../utils/loans'
import {
  CAR_OWNER_LABEL, REFUND_STATUS_LABEL, REFUND_STATUS_TONE, isNegative, nextActions, parseDateList,
  type Money, type Refund, type RefundList, type RefundOptions, type Sweep, type SweepDay, type SweepPerson,
} from '../utils/refunds'

type Access = { allowed: boolean; can_approve: boolean; linked: boolean; person_id: string | null }
type Tab = 'owed' | 'refunds' | 'exposure'

// plain: a figure that is negative by nature (Uber's fee is a deduction) is not a
// warning, so it is not drawn in red. Red is kept for money that reached Fleetly
// as a negative, which is what a person deciding a refund needs to see.
function Zar({ v, strong, plain }: { v: Money; strong?: boolean; plain?: boolean }) {
  return (
    <span className={cn('tabular-nums', !plain && isNegative(v) && 'text-rose-700 dark:text-rose-300', strong && 'font-semibold')}>
      {formatZar(v)}
    </span>
  )
}

export default function DriverRefunds() {
  const accessQuery = useGetRefundsAccess<Access>()
  const sweepQuery = useGetRefundSweep<Sweep>()
  const listQuery = useGetRefunds<RefundList>()
  const optionsQuery = useGetRefundOptions<RefundOptions>()

  const [params, setParams] = useSearchParams()
  const [tab, setTab] = useState<Tab>((params.get('tab') as Tab) || 'owed')
  const [search, setSearch] = useState(params.get('q') ?? '')
  const [notice, setNotice] = useState<string | null>(null)
  const [claiming, setClaiming] = useState(false)

  const loadAccess = accessQuery.trigger
  const loadSweep = sweepQuery.trigger
  const loadList = listQuery.trigger
  const loadOptions = optionsQuery.trigger

  useEffect(() => { void loadAccess({}, { skipCache: true }).catch(() => undefined) }, [loadAccess])
  const access = accessQuery.data
  const allowed = access?.allowed === true

  const refresh = useCallback(() => {
    void loadSweep({}, { skipCache: true }).catch(() => undefined)
    void loadList({}, { skipCache: true }).catch(() => undefined)
  }, [loadSweep, loadList])

  useEffect(() => { if (allowed) refresh() }, [allowed, refresh])
  useEffect(() => { if (allowed && claiming) void loadOptions({}, { skipCache: true }).catch(() => undefined) }, [allowed, claiming, loadOptions])

  function chooseTab(t: Tab) {
    setTab(t)
    const next = new URLSearchParams(params)
    next.set('tab', t)
    setParams(next, { replace: true })
  }

  if (accessQuery.loading && !accessQuery.data && !accessQuery.error) {
    return <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Checking access…</div>
  }
  if (accessQuery.error) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="max-w-md space-y-3 rounded-lg border border-border bg-card p-6 text-center text-sm">
          <p className="text-rose-700 dark:text-rose-300">{accessQuery.error}</p>
          <Button size="sm" variant="outline" onClick={() => void loadAccess({}, { skipCache: true }).catch(() => undefined)}>Try again</Button>
        </div>
      </div>
    )
  }
  if (!allowed) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-foreground">Driver refunds are open to Luyanda, the driver managers, Yolisa and Xolile.</p>
          <p className="text-xs text-muted-foreground">
            {access?.linked === false
              ? 'Your login is not linked to a person in cockpit access. Ask an admin to link it.'
              : 'If you should be one of them, ask an admin to switch on Raise driver refunds for you in cockpit access.'}
          </p>
        </div>
      </div>
    )
  }

  const sweep = sweepQuery.data
  const list = listQuery.data
  const caps = list?.caps ?? { can_raise: true, can_approve: access?.can_approve === true }
  const openRefunds = (list?.rows ?? []).filter((r) => r.status !== 'rejected' && r.status !== 'paid').length

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
          <HandHeart className="h-5 w-5" />
        </div>
        <div className="min-w-[12rem] flex-1">
          <h1 className="text-lg font-semibold text-foreground">Driver Refunds</h1>
          <p className="text-xs text-muted-foreground">
            Uber earnings from trips in a car that is not in the Fleetly fleet, which Uber paid to Fleetly and belong to the person who drove; and
            cash deducted from a closed payslip as a shortfall that has since been proven on Driver cash.
            {sweep?.as_of ? ` Uber reports loaded to ${formatDay(sweep.as_of)}.` : ''}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={refresh} disabled={sweepQuery.loading || listQuery.loading}>
          {sweepQuery.loading || listQuery.loading ? <Spinner className="mr-1 h-4 w-4" /> : <RefreshCw className="mr-1 h-4 w-4" />} Refresh
        </Button>
        <Button variant="brand" size="sm" onClick={() => setClaiming(true)}>
          <HandHeart className="mr-1 h-4 w-4" /> Record a claim
        </Button>
      </div>

      {sweep && (
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2">
          <Chip tone="brand">{formatZar(sweep.totals.unrefunded_zar)} owed and not yet on a refund</Chip>
          <Chip tone="neutral">{sweep.totals.people} people, {sweep.totals.days} days</Chip>
          <Chip tone="neutral">{formatZar(sweep.totals.positive_zar)} on positive days</Chip>
          {sweep.totals.negative_days > 0 && (
            <Chip tone="red" icon={<AlertTriangle className="h-3 w-3" />}>
              {sweep.totals.negative_days} negative {sweep.totals.negative_days === 1 ? 'day' : 'days'}, {formatZar(sweep.totals.negative_zar)}
            </Chip>
          )}
          {sweep.totals.days_without_figure > 0 && (
            <Chip tone="amber">{sweep.totals.days_without_figure} {sweep.totals.days_without_figure === 1 ? 'day has' : 'days have'} no Uber figure</Chip>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-1 border-b border-border px-4 py-2">
        {([
          ['owed', 'Owed'],
          ['refunds', `Refunds${openRefunds ? ` (${openRefunds} open)` : ''}`],
          ['exposure', `On our Uber account${sweep ? ` (${sweep.exposure.length})` : ''}`],
        ] as [Tab, string][]).map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => chooseTab(k)}
            className={cn('rounded-md px-3 py-1.5 text-sm', tab === k ? 'bg-muted font-medium text-foreground' : 'text-muted-foreground hover:text-foreground')}
          >
            {label}
          </button>
        ))}
        {tab === 'owed' && (
          <Input className="ml-auto h-8 w-56 text-xs" placeholder="Find a person" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Find a person" />
        )}
      </div>

      {notice && (
        <div className="flex items-center gap-2 border-b border-border bg-muted/40 px-4 py-2 text-xs text-foreground">
          <span className="flex-1">{notice}</span>
          <button type="button" aria-label="Dismiss" onClick={() => setNotice(null)}><X className="h-3.5 w-3.5" /></button>
        </div>
      )}
      {(sweepQuery.error || listQuery.error) && (
        <div className="border-b border-border bg-rose-50 px-4 py-2 text-sm text-rose-800 dark:bg-rose-950 dark:text-rose-200">
          {sweepQuery.error || listQuery.error}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {tab === 'owed' && (
          sweepQuery.loading && !sweep
            ? <Loading what="the sweep" />
            : sweep && <OwedTab sweep={sweep} search={search} onRaised={(m) => { setNotice(m); refresh(); chooseTab('refunds') }} />
        )}
        {tab === 'refunds' && (
          listQuery.loading && !list
            ? <Loading what="refunds" />
            : list && <RefundsTab rows={list.rows} caps={caps} me={access?.person_id ?? null} onChanged={(m) => { setNotice(m); refresh() }} />
        )}
        {tab === 'exposure' && sweep && <ExposureTab sweep={sweep} />}
      </div>

      <ClaimDialog
        open={claiming}
        onOpenChange={setClaiming}
        options={optionsQuery.data ?? null}
        onSaved={(m) => { setClaiming(false); setNotice(m); refresh(); chooseTab('refunds') }}
      />
    </div>
  )
}

function Loading({ what }: { what: string }) {
  return <p className="flex items-center justify-center gap-2 px-4 py-8 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Loading {what}…</p>
}

// ---------------------------------------------------------------------------
// Owed: one row per person, opening to the days and the windows under each day
// ---------------------------------------------------------------------------
function OwedTab({ sweep, search, onRaised }: { sweep: Sweep; search: string; onRaised: (m: string) => void }) {
  const [open, setOpen] = useState<string | null>(null)
  const q = search.trim().toLowerCase()
  const people = sweep.people.filter((p) => !q || p.person_name.toLowerCase().includes(q))
  const byPerson = useMemo(() => {
    const m = new Map<string, SweepDay[]>()
    for (const d of sweep.days) m.set(d.uber_driver_id, [...(m.get(d.uber_driver_id) ?? []), d])
    return m
  }, [sweep.days])

  // A search that narrows to one person opens that person, so typing a name is
  // enough to see what they are owed.
  const single = people.length === 1 ? people[0].uber_driver_id : null
  useEffect(() => { if (single) setOpen(single) }, [single])

  if (people.length === 0) {
    return <div className="px-4 py-10 text-center text-sm text-muted-foreground">{q ? 'Nobody matches that name.' : 'The sweep found nobody.'}</div>
  }
  return (
    <ul className="divide-y divide-border">
      {people.map((p) => (
        <PersonRow
          key={p.uber_driver_id}
          p={p}
          days={byPerson.get(p.uber_driver_id) ?? []}
          open={open === p.uber_driver_id}
          onToggle={() => setOpen(open === p.uber_driver_id ? null : p.uber_driver_id)}
          onRaised={onRaised}
        />
      ))}
    </ul>
  )
}

function PersonRow({ p, days, open, onToggle, onRaised }: {
  p: SweepPerson; days: SweepDay[]; open: boolean; onToggle: () => void; onRaised: (m: string) => void
}) {
  const raise = useRaiseRefund<{ ref: string; status: string; matched_days: string[] }>()
  const [error, setError] = useState<string | null>(null)

  async function doRaise() {
    setError(null)
    try {
      const r = await raise.trigger({ source: 'sweep', uber_driver_id: p.uber_driver_id })
      if (r) onRaised(`${r.ref} raised for ${p.person_name}, ${r.matched_days.length} ${r.matched_days.length === 1 ? 'day' : 'days'}. The facts now need to be verified.`)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  return (
    <li>
      <button type="button" onClick={onToggle} className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-left hover:bg-muted/40">
        {open ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
        <span className="min-w-[12rem] flex-1">
          <span className="block text-sm font-medium text-foreground">{p.person_name}</span>
          <span className="block text-xs text-muted-foreground">
            {p.pipeline_stage}{p.ever_a_driver ? ', has a drivers record' : ', never a Fleetly driver'}. {p.days} {p.days === 1 ? 'day' : 'days'}, {formatDay(p.first_day)}{p.last_day !== p.first_day ? ` to ${formatDay(p.last_day)}` : ''}.
          </span>
        </span>
        <span className="text-right">
          <span className="block text-sm"><Zar v={p.unrefunded_zar ?? (p.days_without_figure === p.days ? null : 0)} strong /></span>
          <span className="block text-[11px] text-muted-foreground">owed, not yet on a refund</span>
        </span>
        <span className="flex flex-wrap gap-1">
          {p.negative_days > 0 && <Chip tone="red">{p.negative_days} negative</Chip>}
          {p.days_without_figure > 0 && <Chip tone="amber">{p.days_without_figure} without a figure</Chip>}
          {p.attributed_days > 0 && <Chip tone="amber">{p.attributed_days} inside a fleet shift</Chip>}
          <Chip tone={p.bank_letter_verified ? 'green' : 'amber'}>{p.bank_letter_verified ? 'Bank letter verified' : 'No bank letter'}</Chip>
          {(p.refund_refs ?? []).map((ref) => <Chip key={ref} tone="brand">{ref}</Chip>)}
        </span>
      </button>

      {open && (
        <div className="space-y-3 bg-muted/20 px-4 pb-4 pt-1">
          <p className="text-xs text-muted-foreground">
            Identity: {p.identity_basis}. First Uber day on file for this account: {formatDay(p.first_uber_day_on_file)}. Uber does not record when an account was linked to the Fleetly fleet, or by whom.
          </p>
          <div className="overflow-x-auto rounded-md border border-border bg-card">
            <table className="w-full min-w-[46rem] text-xs">
              <thead className="bg-muted/50 text-left text-muted-foreground">
                <tr>
                  <th className="px-2 py-1.5 font-medium">Day</th>
                  <th className="px-2 py-1.5 font-medium">Plate</th>
                  <th className="px-2 py-1.5 text-right font-medium">Trips</th>
                  <th className="px-2 py-1.5 text-right font-medium">Gross fares</th>
                  <th className="px-2 py-1.5 text-right font-medium">Uber fees and tax</th>
                  <th className="px-2 py-1.5 text-right font-medium">Net earnings</th>
                  <th className="px-2 py-1.5 text-right font-medium">Cash he holds</th>
                  <th className="px-2 py-1.5 text-right font-medium">Paid to Fleetly</th>
                  <th className="px-2 py-1.5 font-medium">Refund</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {days.map((d) => (
                  <DayRows key={d.shift_date} d={d} />
                ))}
              </tbody>
              <tfoot className="border-t border-border bg-muted/30">
                <tr>
                  <td className="px-2 py-1.5 font-medium" colSpan={7}>Total paid to Fleetly for these days</td>
                  <td className="px-2 py-1.5 text-right"><Zar v={p.net_paid_to_fleetly_zar} strong /></td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
          {p.negative_days > 0 && (
            <p className="text-xs text-rose-700 dark:text-rose-300">
              A negative day is one where the cash collected from riders was more than Uber paid Fleetly. The total above nets it against the other days. Whoever verifies the refund decides which days it covers.
            </p>
          )}
          {error && <p className="text-xs text-rose-700 dark:text-rose-300">{error}</p>}
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="brand" disabled={raise.loading || !p.person_id || p.unrefunded_days === 0} onClick={() => void doRaise()}>
              {raise.loading ? <Spinner className="mr-1 h-4 w-4" /> : <HandHeart className="mr-1 h-4 w-4" />} Raise a refund for these days
            </Button>
            {!p.person_id && <span className="text-xs text-amber-700 dark:text-amber-300">This Uber account is not linked to a person, so a refund could not carry a name. Link it first.</span>}
            {p.person_id && p.unrefunded_days === 0 && <span className="text-xs text-muted-foreground">Every day with a figure is already on a refund.</span>}
          </div>
        </div>
      )}
    </li>
  )
}

function DayRows({ d }: { d: SweepDay }) {
  const windows = d.window_rows ?? []
  const negativeWindow = windows.some((w) => isNegative(w.net_paid_to_fleetly_zar))
  return (
    <>
      <tr className={cn(d.is_negative && 'bg-rose-50/60 dark:bg-rose-950/30')}>
        <td className="px-2 py-1.5 whitespace-nowrap">{formatDay(d.shift_date)}</td>
        <td className="px-2 py-1.5">{d.plates ?? '—'}</td>
        <td className="px-2 py-1.5 text-right tabular-nums">{d.trips ?? '—'}</td>
        <td className="px-2 py-1.5 text-right"><Zar v={d.gross_fares_zar} /></td>
        <td className="px-2 py-1.5 text-right"><Zar v={d.uber_fees_zar} plain /></td>
        <td className="px-2 py-1.5 text-right"><Zar v={d.net_earnings_zar} /></td>
        <td className="px-2 py-1.5 text-right"><Zar v={d.cash_collected_zar} /></td>
        <td className="px-2 py-1.5 text-right"><Zar v={d.net_paid_to_fleetly_zar} strong /></td>
        <td className="px-2 py-1.5">{d.refund_ref ? <Chip tone={d.refund_status ? REFUND_STATUS_TONE[d.refund_status] : 'neutral'}>{d.refund_ref}</Chip> : '—'}</td>
      </tr>
      {(windows.length > 1 || negativeWindow) && windows.map((w) => (
        <tr key={w.shift_window} className="text-muted-foreground">
          <td className="py-1 pl-6 pr-2">{w.shift_window === 'am_pm' ? 'Day window' : 'Night window'}</td>
          <td className="px-2 py-1">{w.plates ?? '—'}</td>
          <td className="px-2 py-1 text-right tabular-nums">{w.trips ?? '—'}</td>
          <td colSpan={2} />
          <td className="px-2 py-1 text-right"><Zar v={w.net_earnings_zar} /></td>
          <td className="px-2 py-1 text-right"><Zar v={w.cash_collected_zar} /></td>
          <td className="px-2 py-1 text-right"><Zar v={w.net_paid_to_fleetly_zar} /></td>
          <td />
        </tr>
      ))}
      {(d.figure_note || d.attribution_note) && (
        <tr>
          <td colSpan={9} className="px-2 pb-1.5 text-[11px] text-muted-foreground">
            {d.figure_note}
            {d.attribution_note && (
              <span className="block text-amber-700 dark:text-amber-300">
                {d.attribution_note} Shift: {d.shift_that_day}.{d.owner_statements_that_day ? ` Statement: ${d.owner_statements_that_day}.` : ''}
              </span>
            )}
          </td>
        </tr>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Refunds: raised, verified, approved, staged, paid
// ---------------------------------------------------------------------------
function RefundsTab({ rows, caps, me, onChanged }: {
  rows: Refund[]; caps: { can_raise: boolean; can_approve: boolean }; me: string | null; onChanged: (m: string) => void
}) {
  if (rows.length === 0) {
    return <div className="px-4 py-10 text-center text-sm text-muted-foreground">No refunds yet. Raise one from the Owed tab, or record a claim.</div>
  }
  return <ul className="divide-y divide-border">{rows.map((r) => <RefundItem key={r.id} r={r} caps={caps} me={me} onChanged={onChanged} />)}</ul>
}

function RefundItem({ r, caps, me, onChanged }: {
  r: Refund; caps: { can_raise: boolean; can_approve: boolean }; me: string | null; onChanged: (m: string) => void
}) {
  const approve = useApproveRefund<{ staged: boolean; message?: string }>()
  const stage = useStageRefundPayment<{ ok: boolean }>()
  const [dialog, setDialog] = useState<null | 'verify' | 'reject' | 'paid'>(null)
  const [error, setError] = useState<string | null>(null)
  // The bank letter is the last thing between an approved refund and the EFT, so the
  // block opens by itself then. Before approval it can be opened to collect the
  // letter early. Once a payment is staged the account is frozen onto it.
  const needsLetter = r.status === 'approved' && !r.payment && !r.bank_letter_verified
  const canHaveLetter = !r.payment && ['raised', 'needs_review', 'verified', 'approved'].includes(r.status)
  const [bankOpen, setBankOpen] = useState(needsLetter)
  const a = nextActions(r, caps, me)
  const activeDays = (r.days ?? []).filter((d) => d.active)
  const moved = r.amount_zar !== null && r.current_total_zar !== null && Number(r.amount_zar) !== Number(r.current_total_zar)
  const cash = r.source === 'cash_proof'

  async function run<T>(fn: () => Promise<T>, done: (t: T) => string) {
    setError(null)
    try { onChanged(done(await fn())) } catch (e) { setError(e instanceof Error ? e.message : String(e)) }
  }

  return (
    <li className="space-y-2 px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-xs text-muted-foreground">{r.ref}</span>
        <span className="text-sm font-medium text-foreground">{r.person_name}</span>
        <Chip tone={REFUND_STATUS_TONE[r.status]}>{cash && r.status === 'raised' ? 'Waiting for approval' : REFUND_STATUS_LABEL[r.status]}</Chip>
        <Chip tone="neutral">{cash ? 'Cash shortfall proven' : r.source === 'claim' ? 'From a claim' : 'From the sweep'}</Chip>
        <span className="ml-auto text-right">
          <span className="block text-sm"><Zar v={r.amount_zar ?? r.current_total_zar} strong /></span>
          <span className="block text-[11px] text-muted-foreground">
            {cash
              ? r.status === 'raised' ? 'proven by the slips on Driver cash, waiting for approval' : 'proven cash, deducted on a closed payslip'
              : r.amount_zar !== null ? 'verified Uber figure' : 'Uber figure today, not yet verified'}
          </span>
        </span>
      </div>

      <div className="grid gap-1 text-xs text-muted-foreground sm:grid-cols-2">
        <p>
          {cash ? 'Shifts' : 'Days'}: {activeDays.length ? activeDays.map((d) => `${formatDay(d.shift_date)} (${formatZar(d.net_paid_zar)})`).join(', ') : 'none matched'}
        </p>
        {cash && r.deduction_note && <p>{r.deduction_note}</p>}
        {r.source === 'claim' && (
          <p>
            Claimed: {(r.claimed_dates ?? []).map(formatDay).join(', ')}{r.claimed_plate ? `, plate ${r.claimed_plate}` : ''}, {r.car_owner ? CAR_OWNER_LABEL[r.car_owner] ?? r.car_owner : 'car not stated'}{r.car_owner_note ? ` (${r.car_owner_note})` : ''}.
          </p>
        )}
        <p>Raised by {r.raised_by ?? '—'}, {formatWhen(r.raised_at)}.</p>
        {r.verified_by && <p>Facts verified by {r.verified_by}, {formatWhen(r.verified_at)}: {r.verify_note}</p>}
        {r.approved_by && <p>Amount approved by {r.approved_by}, {formatWhen(r.approved_at)}.</p>}
        {r.rejected_by && <p className="text-rose-700 dark:text-rose-300">Rejected by {r.rejected_by}, {formatWhen(r.rejected_at)}: {r.rejection_reason}</p>}
      </div>

      {r.review_reason && <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-200">{r.review_reason}</p>}
      {moved && (
        <p className="text-xs text-amber-700 dark:text-amber-300">
          {cash ? 'The proof on Driver cash now comes to' : "Uber's figure for these days is now"} {formatZar(r.current_total_zar)}. Verify again before approving.
        </p>
      )}

      {r.status === 'approved' && !r.payment && (
        <p className="text-xs text-amber-700 dark:text-amber-300">
          Owed and waiting for a bank letter. {r.bank_letter_verified ? 'A verified letter is now on file, so the payment can be staged.' : 'No verified bank confirmation letter is on file for this person, so nothing can be paid yet. Upload it below.'}
        </p>
      )}
      {canHaveLetter && bankOpen && (
        <BankDetailsPanel refundId={r.id} onChanged={() => onChanged(`${r.ref}: bank account set from the letter.${r.status === 'approved' ? ' The payment can now be staged.' : ''}`)} />
      )}
      {r.payment && (
        <p className="text-xs text-foreground">
          EFT {r.payment.status === 'paid' ? `paid on ${formatDay(r.payment.paid_on)}, reference ${r.payment.payment_reference}` : 'staged, not yet paid'}: {formatZar(r.payment.amount_zar)} to {r.payment.account_holder ?? 'the account holder'}, {r.payment.bank_key ?? 'bank'} account ending {r.payment.account_tail ?? '—'}, bank letter {r.payment.bank_letter_id.slice(0, 8)}, covering {r.payment.days} {r.payment.days === 1 ? 'day' : 'days'} from {formatDay(r.payment.first_day)} to {formatDay(r.payment.last_day)}.
        </p>
      )}
      {(r.messages ?? []).length > 0 && (
        <p className="text-[11px] text-muted-foreground">
          Messages: {(r.messages ?? []).map((m) => `${m.template_key.replace('driver_refund_', '')} (${m.status})`).join(', ')}.
          {(r.messages ?? []).some((m) => m.status === 'held') && ' Held messages are not sent until refund messages are switched on.'}
          {!r.person_email && ' No email address is on file for this person.'}
        </p>
      )}

      {error && <p className="text-xs text-rose-700 dark:text-rose-300">{error}</p>}
      <div className="flex flex-wrap gap-2">
        {a.verify && <Button size="sm" variant="outline" onClick={() => setDialog('verify')}><Check className="mr-1 h-4 w-4" /> {r.status === 'verified' ? 'Verify again' : 'Verify the facts'}</Button>}
        {a.approve && (
          <Button size="sm" variant="brand" disabled={approve.loading} onClick={() => void run(
            () => approve.trigger({ id: r.id }),
            (x) => `${r.ref} approved. ${x?.staged ? 'The EFT is staged.' : 'It waits for a verified bank letter before it can be staged.'} ${x?.message ?? ''}`,
          )}>
            {approve.loading ? <Spinner className="mr-1 h-4 w-4" /> : <Check className="mr-1 h-4 w-4" />} Approve {formatZar(r.amount_zar)}
          </Button>
        )}
        {a.approveBlockedBecauseMaker && <span className="self-center text-xs text-muted-foreground">You verified the facts, so another approver must approve the amount.</span>}
        {a.stage && (
          <Button size="sm" variant="outline" disabled={stage.loading} onClick={() => void run(() => stage.trigger({ id: r.id }), () => `${r.ref}: the EFT is staged.`)}>
            Stage the payment
          </Button>
        )}
        {a.markPaid && <Button size="sm" variant="outline" onClick={() => setDialog('paid')}>Record the EFT as paid</Button>}
        {canHaveLetter && !needsLetter && (
          <Button size="sm" variant="ghost" onClick={() => setBankOpen((o) => !o)}>
            {bankOpen ? 'Hide bank details' : r.bank_letter_verified ? 'Bank details' : 'Add a bank letter'}
          </Button>
        )}
        {a.reject && <Button size="sm" variant="ghost" onClick={() => setDialog('reject')}>Reject</Button>}
      </div>

      {dialog === 'verify' && <VerifyDialog r={r} onClose={() => setDialog(null)} onDone={(m) => { setDialog(null); onChanged(m) }} />}
      {dialog === 'reject' && <RejectDialog r={r} onClose={() => setDialog(null)} onDone={(m) => { setDialog(null); onChanged(m) }} />}
      {dialog === 'paid' && <PaidDialog r={r} onClose={() => setDialog(null)} onDone={(m) => { setDialog(null); onChanged(m) }} />}
    </li>
  )
}

function Shell({ title, description, onClose, children }: { title: string; description: ReactNode; onClose: () => void; children: ReactNode }) {
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  )
}

function VerifyDialog({ r, onClose, onDone }: { r: Refund; onClose: () => void; onDone: (m: string) => void }) {
  const verify = useVerifyRefund<{ amount_zar: Money; days: number }>()
  const all = r.days ?? []
  const [keep, setKeep] = useState<string[]>(all.filter((d) => d.active).map((d) => d.shift_date))
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  async function submit() {
    setError(null)
    try {
      const x = await verify.trigger({ id: r.id, keep_days: keep, note })
      onDone(`${r.ref} verified at ${formatZar(x?.amount_zar ?? null)} for ${x?.days ?? keep.length} days. A second person now approves the amount.`)
    } catch (e) { setError(e instanceof Error ? e.message : String(e)) }
  }
  return (
    <Shell title={`Verify the facts on ${r.ref}`} onClose={onClose}
      description={r.source === 'cash_proof'
        ? 'Open each shift on Driver cash and check that the slip or receipt is for that shift, and that his payslip deducted the cash. The amount is what was proven on the shifts you keep, never more than the payslip took. You cannot approve a refund you verified.'
        : 'Check that the car was not a Fleetly car and that the days match the Uber report. The amount is the Uber figure for the days you keep. You cannot approve a refund you verified.'}>
      <div className="space-y-3">
        <div className="space-y-1">
          {all.map((d) => (
            <label key={d.shift_date} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={keep.includes(d.shift_date)}
                onChange={(e) => setKeep(e.target.checked ? [...keep, d.shift_date] : keep.filter((k) => k !== d.shift_date))} />
              <span className="flex-1">{formatDay(d.shift_date)}{d.plates ? `, ${d.plates}` : ''}</span>
              <Zar v={d.current_zar} />
            </label>
          ))}
        </div>
        <div className="space-y-1">
          <label htmlFor="verify-note" className="text-xs font-medium text-foreground">What you checked</label>
          <Textarea id="verify-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder={r.source === 'cash_proof'
              ? 'For example: opened all four slips, each is an FNB deposit to Fleetly on the shift date; October payslip shows the Cash Shortfall line.'
              : 'For example: LZ50XTGP is his own car, confirmed with him by phone; both days match the Uber report.'} />
        </div>
        {error && <p className="text-xs text-rose-700 dark:text-rose-300">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="brand" size="sm" disabled={verify.loading || keep.length === 0} onClick={() => void submit()}>
            {verify.loading && <Spinner className="mr-1 h-4 w-4" />} Verify
          </Button>
        </div>
      </div>
    </Shell>
  )
}

function RejectDialog({ r, onClose, onDone }: { r: Refund; onClose: () => void; onDone: (m: string) => void }) {
  const reject = useRejectRefund<{ message?: string }>()
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  async function submit() {
    setError(null)
    try {
      const x = await reject.trigger({ id: r.id, reason })
      onDone(`${r.ref} rejected. ${x?.message ?? ''}`)
    } catch (e) { setError(e instanceof Error ? e.message : String(e)) }
  }
  return (
    <Shell title={`Reject ${r.ref}`} onClose={onClose}
      description={r.source === 'cash_proof'
        ? `${r.person_name} reads this reason in the rejection message, so write it to them. The proof stays on Driver cash; only the refund is refused.`
        : `${r.person_name} reads this reason in the rejection message, so write it to them. The days go back to the sweep.`}>
      <div className="space-y-3">
        <Textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Reason"
          placeholder="For example: The Uber report shows these trips in LK36RJGP, which is a Fleetly car." />
        {error && <p className="text-xs text-rose-700 dark:text-rose-300">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="brand" size="sm" disabled={reject.loading} onClick={() => void submit()}>
            {reject.loading && <Spinner className="mr-1 h-4 w-4" />} Reject
          </Button>
        </div>
      </div>
    </Shell>
  )
}

function todaySast(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Johannesburg' })
}

function PaidDialog({ r, onClose, onDone }: { r: Refund; onClose: () => void; onDone: (m: string) => void }) {
  const mark = useMarkRefundPaid<{ message?: string }>()
  const [reference, setReference] = useState('')
  const [paidOn, setPaidOn] = useState(todaySast())
  const [error, setError] = useState<string | null>(null)
  async function submit() {
    setError(null)
    try {
      const x = await mark.trigger({ id: r.id, reference, paid_on: paidOn })
      onDone(`${r.ref} recorded as paid on ${formatDay(paidOn)}. ${x?.message ?? ''}`)
    } catch (e) { setError(e instanceof Error ? e.message : String(e)) }
  }
  return (
    <Shell title={`Record the EFT for ${r.ref}`} onClose={onClose}
      description={`Only once the money has left the bank. ${formatZar(r.payment?.amount_zar ?? null)} to the account ending ${r.payment?.account_tail ?? '—'}.`}>
      <div className="space-y-3">
        <div className="space-y-1">
          <label htmlFor="paid-ref" className="text-xs font-medium text-foreground">EFT reference</label>
          <Input id="paid-ref" value={reference} onChange={(e) => setReference(e.target.value)} />
        </div>
        <div className="space-y-1">
          <label htmlFor="paid-on" className="text-xs font-medium text-foreground">Paid on</label>
          <Input id="paid-on" type="date" value={paidOn} max={todaySast()} onChange={(e) => setPaidOn(e.target.value)} />
        </div>
        {error && <p className="text-xs text-rose-700 dark:text-rose-300">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="brand" size="sm" disabled={mark.loading} onClick={() => void submit()}>
            {mark.loading && <Spinner className="mr-1 h-4 w-4" />} Record as paid
          </Button>
        </div>
      </div>
    </Shell>
  )
}

// ---------------------------------------------------------------------------
// A claim, recorded by staff for the person
// ---------------------------------------------------------------------------
function ClaimDialog({ open, onOpenChange, options, onSaved }: {
  open: boolean; onOpenChange: (o: boolean) => void; options: RefundOptions | null; onSaved: (m: string) => void
}) {
  const raise = useRaiseRefund<{ ref: string; status: string; matched_days: string[]; unmatched_dates: string[]; review_reason: string | null; message: string | null }>()
  const [person, setPerson] = useState('')
  const [dates, setDates] = useState('')
  const [plate, setPlate] = useState('')
  const [owner, setOwner] = useState('')
  const [ownerNote, setOwnerNote] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const parsed = parseDateList(dates)

  useEffect(() => { if (!open) { setPerson(''); setDates(''); setPlate(''); setOwner(''); setOwnerNote(''); setNote(''); setError(null) } }, [open])

  async function submit() {
    setError(null)
    if (parsed.bad.length) { setError(`${parsed.bad.join(', ')} is not a date. Use the form 2026-09-25.`); return }
    try {
      const r = await raise.trigger({
        source: 'claim', person_id: person, claimed_dates: parsed.dates, claimed_plate: plate || null,
        car_owner: owner, car_owner_note: ownerNote || null, claim_note: note || null,
      })
      if (r) {
        onSaved(r.status === 'needs_review'
          ? `${r.ref} recorded. It matched no day in the sweep, so it waits for a person to review it. ${r.review_reason ?? ''}`
          : `${r.ref} recorded and matched ${r.matched_days.length} ${r.matched_days.length === 1 ? 'day' : 'days'} in the sweep. ${r.review_reason ?? ''} ${r.message ?? ''}`)
      }
    } catch (e) { setError(e instanceof Error ? e.message : String(e)) }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Record a claim</DialogTitle>
          <DialogDescription>
            For someone who says Uber paid Fleetly for trips they did in a car that is not ours. The claim is matched against the Uber reports and paid on Uber's figure, never on a figure they give. A claim that matches nothing is kept for review, not refused.
          </DialogDescription>
        </DialogHeader>
        {!options ? <Loading what="people" /> : (
          <div className="space-y-3">
            <div className="space-y-1">
              <label htmlFor="claim-person" className="text-xs font-medium text-foreground">Who is claiming</label>
              <NativeSelect id="claim-person" className="w-full" value={person} onChange={(e) => setPerson(e.target.value)}>
                <option value="">Choose a person</option>
                {options.people.map((p) => <option key={p.person_id} value={p.person_id}>{p.name}</option>)}
              </NativeSelect>
              <p className="text-[11px] text-muted-foreground">Only people whose Uber account we can tie to a name are listed, because a claim is matched on the Uber account.</p>
            </div>
            <div className="space-y-1">
              <label htmlFor="claim-dates" className="text-xs font-medium text-foreground">The days they drove</label>
              <Input id="claim-dates" value={dates} onChange={(e) => setDates(e.target.value)} placeholder="2026-09-25, 2026-09-26" />
              {parsed.dates.length > 0 && <p className="text-[11px] text-muted-foreground">{parsed.dates.map(formatDay).join(', ')}</p>}
            </div>
            <div className="space-y-1">
              <label htmlFor="claim-plate" className="text-xs font-medium text-foreground">Plate of the car they used</label>
              <Input id="claim-plate" value={plate} onChange={(e) => setPlate(e.target.value)} placeholder="LZ50XTGP" />
            </div>
            <div className="space-y-1">
              <label htmlFor="claim-owner" className="text-xs font-medium text-foreground">Whose car it was</label>
              <NativeSelect id="claim-owner" className="w-full" value={owner} onChange={(e) => setOwner(e.target.value)}>
                <option value="">Choose one</option>
                {options.car_owners.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </NativeSelect>
              {owner === 'other' && <Input className="mt-1" value={ownerNote} onChange={(e) => setOwnerNote(e.target.value)} placeholder="Whose car it was" aria-label="Whose car it was" />}
            </div>
            <div className="space-y-1">
              <label htmlFor="claim-note" className="text-xs font-medium text-foreground">Anything else they told you</label>
              <Textarea id="claim-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="If they sent a screenshot of their Uber earnings, say where it is kept." />
            </div>
            {error && <p className="text-xs text-rose-700 dark:text-rose-300">{error}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button variant="brand" size="sm" disabled={raise.loading || !person || parsed.dates.length === 0 || !owner} onClick={() => void submit()}>
                {raise.loading && <Spinner className="mr-1 h-4 w-4" />} Record the claim
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// On our Uber account, not an active driver: the exposure going forward
// ---------------------------------------------------------------------------
function ExposureTab({ sweep }: { sweep: Sweep }) {
  const asOf = sweep.exposure[0]?.roster_as_of ?? null
  return (
    <div className="space-y-2 px-4 py-3">
      <p className="text-xs text-muted-foreground">
        Everyone the Uber roster shows as active on the Fleetly fleet account who is not an active Fleetly driver. Any of them can drive their own car on our account and have Uber pay Fleetly for it.
        {asOf ? ` The roster was last loaded on ${formatDay(asOf)}.` : ''} Uber's reports do not say when an account was linked to the fleet, or by whom.
      </p>
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full min-w-[44rem] text-xs">
          <thead className="bg-muted/50 text-left text-muted-foreground">
            <tr>
              <th className="px-2 py-1.5 font-medium">Person</th>
              <th className="px-2 py-1.5 font-medium">Fleetly status</th>
              <th className="px-2 py-1.5 font-medium">Pipeline</th>
              <th className="px-2 py-1.5 font-medium">First Uber day on file</th>
              <th className="px-2 py-1.5 font-medium">Last Uber day on file</th>
              <th className="px-2 py-1.5 text-right font-medium">Days not in a fleet car</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sweep.exposure.map((e) => (
              <tr key={e.uber_driver_id}>
                <td className="px-2 py-1.5">{e.person_name}{e.identity_basis === 'not linked to anyone' ? ' (not linked to a person)' : ''}</td>
                <td className="px-2 py-1.5">{e.driver_status}</td>
                <td className="px-2 py-1.5">{e.pipeline_stage}</td>
                <td className="px-2 py-1.5">{formatDay(e.first_uber_day_on_file)}</td>
                <td className="px-2 py-1.5">{formatDay(e.last_uber_day_on_file)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{e.non_fleet_days || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
